import React, { useState, useEffect, useMemo } from 'react'; // Added useEffect, useMemo
import MoradorList from '../components/MoradorList';
import MoradorForm from '../components/MoradorFormV2';
import api from '../services/api'; // Added api import
import { Container, Typography, Box, Button, IconButton, Collapse, TextField, Autocomplete, CircularProgress, FormControlLabel, Switch } from '@mui/material'; // Added IconButton, Collapse, TextField, Autocomplete, CircularProgress
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList'; // New import
import { debounce } from 'lodash'; // Added debounce
import { actionButtonSx, filterCardSx, pageTitleSx } from '../styles/uiTokens';

function MoradoresPage() {
  const [refreshList, setRefreshList] = useState(false);
  const [editingMorador, setEditingMorador] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showFilters, setShowFilters] = useState(false); // State for filter visibility

  // Filter states
  const [moradorNameFilter, setMoradorNameFilter] = useState('');
  const [selectedImovelIdFilter, setSelectedImovelIdFilter] = useState('');
  const [showAll, setShowAll] = useState(false);

  // Autocomplete states for Imovel filter
  const [imovelOptions, setImovelOptions] = useState([]);
  const [loadingImoveis, setLoadingImoveis] = useState(false);
  const [selectedImovelFilter, setSelectedImovelFilter] = useState(null); // Holds the selected Imovel object

  // Debounced fetch for Imoveis
  const debouncedFetchImoveis = useMemo(() => debounce((searchTerm) => {
      setLoadingImoveis(true);
      api.get(`/imoveis?search=${encodeURIComponent(searchTerm)}&page=0&limit=20`)
        .then(response => setImovelOptions(response.data.data || response.data))
        .catch(error => console.error('Erro ao buscar imoveis para filtro:', error))
        .finally(() => setLoadingImoveis(false));
    }, 500),
    []
  );
  useEffect(() => {
    return () => {
      debouncedFetchImoveis.cancel();
    };
  }, [debouncedFetchImoveis]);

  // Effect to trigger list refresh when filters change
      useEffect(() => {
        // console.log('MoradoresPage: setRefreshList triggered by filter change.', { moradorNameFilter, selectedImovelIdFilter });
        setRefreshList(prev => !prev);
      }, [moradorNameFilter, selectedImovelIdFilter, showAll]);

  const handleMoradorAdded = () => {
    setRefreshList(prev => !prev);
    setEditingMorador(null);
    setShowForm(false);
  };

  const handleEditMorador = (morador) => {
    setEditingMorador(morador);
    setShowForm(true);
  };

  const handleCancelEditMorador = () => {
    setEditingMorador(null);
    setShowForm(false);
  };

  const handleMoradorDeleted = () => {
    setRefreshList(prev => !prev);
  };

  const handleShowForm = () => {
    setEditingMorador(null);
    setShowForm(true);
  };

  return (
    <Container sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" gutterBottom sx={pageTitleSx}>
          Gerenciar Moradores
        </Typography>
        <Box> {/* Group buttons */}
          {!showForm && (
            <>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleShowForm}
                sx={{ ...actionButtonSx, mr: 1 }} // Add margin right
              >
                Adicionar Morador
              </Button>
              <IconButton
                onClick={() => setShowFilters(prev => !prev)}
                aria-label="toggle filters"
                sx={{ color: 'primary.main' }}
              >
                <FilterListIcon />
              </IconButton>
            </>
          )}
        </Box>
      </Box>

      {!showForm && ( // Only show filters if form is not active
        <Collapse in={showFilters}>
          <Box sx={filterCardSx}>
            <Typography variant="h6" gutterBottom>Filtros</Typography>
            <TextField
              label="Nome do Morador"
              fullWidth
              margin="normal"
              value={moradorNameFilter}
              onChange={(e) => {
                setMoradorNameFilter(e.target.value);
                // console.log('MoradoresPage: moradorNameFilter changed to', e.target.value);
              }}
            />
            <Autocomplete
              id="imovel-filter-autocomplete"
              options={imovelOptions}
              filterOptions={(options) => options}
              getOptionLabel={(option) => `${option.descricao} - ${option.numero}` || ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={selectedImovelFilter}
              onChange={(event, newValue) => {
                setSelectedImovelFilter(newValue);
                const newImovelId = newValue ? newValue.id : '';
                setSelectedImovelIdFilter(newImovelId);
                // console.log('MoradoresPage: selectedImovelIdFilter changed to', newImovelId);
              }}
              onInputChange={(event, newInputValue, reason) => {
                // console.log('MoradoresPage: Autocomplete Imovel onInputChange - newInputValue:', newInputValue, 'reason:', reason);
                if (reason === 'input') {
                  if (newInputValue) {
                    debouncedFetchImoveis(newInputValue);
                  } else {
                    setImovelOptions([]);
                  }
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Residencia (Imovel)"
                  variant="outlined"
                  margin="normal"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingImoveis ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <FormControlLabel
              control={<Switch checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />}
              label="Exibir todos (inclui ex-moradores)"
              sx={{ mt: 2 }}
            />
          </Box>
        </Collapse>
      )}

      {showForm && (
        <MoradorForm
          onMoradorAdded={handleMoradorAdded}
          initialData={editingMorador}
          onCancelEdit={handleCancelEditMorador}
        />
      )}

      {!showForm && (
        <MoradorList
          refresh={refreshList}
          onEdit={handleEditMorador}
          onMoradorDeleted={handleMoradorDeleted}
          moradorNameFilter={moradorNameFilter}
          imovelIdFilter={selectedImovelIdFilter}
          showAll={showAll}
        />
      )}
    </Container>
  );
}

export default MoradoresPage;

